import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { sanitizeNumericInput } from "./numeric-input-utils";

import { Input } from "@/components/ui/input";

interface BetweenNumberValueInputProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  type?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  minPlaceholder?: string;
  maxPlaceholder?: string;
  allowCurrencyPrefix?: boolean;
  allowUnitSuffix?: boolean;
}

const parseRangeValue = (raw: string): { min: string; max: string } => {
  if (!raw.trim()) {
    return { min: "", max: "" };
  }
  if (raw.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return {
          min: String(parsed[0] ?? ""),
          max: String(parsed[1] ?? ""),
        };
      }
    } catch {
      return { min: "", max: "" };
    }
  }

  if (raw.includes(" - ")) {
    const parts = raw.split(" - ");
    return {
      min: parts[0]?.trim() ?? "",
      max: parts[1]?.trim() ?? "",
    };
  }

  const parts = raw.split(",");
  return {
    min: parts[0]?.trim() ?? "",
    max: parts[1]?.trim() ?? "",
  };
};

const BetweenNumberValueInput = ({
  inputRef,
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  type = "number",
  inputMode,
  minPlaceholder = "Min",
  maxPlaceholder = "Max",
  allowCurrencyPrefix = false,
  allowUnitSuffix = false,
}: BetweenNumberValueInputProps) => {
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const parsedFromValue = useMemo(() => parseRangeValue(value), [value]);

  useEffect(() => {
    setMinValue(parsedFromValue.min);
    setMaxValue(parsedFromValue.max);
  }, [parsedFromValue.max, parsedFromValue.min]);

  const emitCombinedValue = useCallback(
    (nextMin: string, nextMax: string) => {
      const event = {
        target: {
          value: `${nextMin},${nextMax}`,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    },
    [onChange],
  );

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextMin = sanitizeNumericInput(e.target.value, {
        allowCurrencyPrefix,
        allowUnitSuffix,
      });
      setMinValue(nextMin);
      emitCombinedValue(nextMin, maxValue);
    },
    [allowCurrencyPrefix, allowUnitSuffix, emitCombinedValue, maxValue],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextMax = sanitizeNumericInput(e.target.value, {
        allowCurrencyPrefix,
        allowUnitSuffix,
      });
      setMaxValue(nextMax);
      emitCombinedValue(minValue, nextMax);
    },
    [allowCurrencyPrefix, allowUnitSuffix, emitCombinedValue, minValue],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const nextTarget = e.relatedTarget as Node | null;
      if (nextTarget && rootRef.current?.contains(nextTarget)) {
        return;
      }

      onBlur();
    },
    [onBlur],
  );

  return (
    <div ref={rootRef} className="flex min-w-[248px] items-center gap-2">
      <Input
        ref={inputRef as React.Ref<HTMLInputElement>}
        type={type}
        inputMode={inputMode}
        value={minValue}
        onChange={handleMinChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder={minPlaceholder}
        className="h-8 w-[116px] min-w-[116px] shrink-0 border-0 bg-transparent px-2 focus-visible:ring-0"
      />
      <span className="shrink-0 text-sm text-gray-400">-</span>
      <Input
        type={type}
        inputMode={inputMode}
        value={maxValue}
        onChange={handleMaxChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder={maxPlaceholder}
        className="h-8 w-[116px] min-w-[116px] shrink-0 border-0 bg-transparent px-2 focus-visible:ring-0"
      />
    </div>
  );
};

export default BetweenNumberValueInput;
